import dayjs from 'dayjs';
import {action, computed, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {TextInput} from '../../../../../uikit/TextInput';
import type {GetComponentProps} from '../../../../../utils/utility-types';
import type {CRUConferenceConfig, ScheduledConference} from '../types';

import {currentUserStore} from '../../../../../api/user';
import {getSecondsFromMidnight} from '../../../../../utils/datetime';
import {CRUProgramScheduleEventStore} from './CRUProgramScheduleEvent';

export class CRUProgramScheduleConferenceStore extends CRUProgramScheduleEventStore {
  static DURATION_HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  static DURATION_MINUTES = [0, 15, 30, 45];
  durationTime = [1, 0];

  constructor(config: CRUConferenceConfig) {
    super(config);

    const {conference} = config || {};

    if (conference) {
      this.fields.title.value = conference.title;
      this.fields.description.value = conference.description;
      this.day = conference.day || this.day;

      this.date = dayjs(this.date)
        .startOf('day')
        .add(conference.time, 'second')
        .toDate();
      this.groupUuid = conference.uuid || this.groupUuid;
      this.uuid = conference.edition_uuid || this.uuid;
      this.repeat =
        this.availableRepeats.find(
          repeat => conference.repeat.days === repeat.interval,
        ) || this.repeat;
      this.initialRepeat = this.repeat;

      const hours = Math.floor(conference.duration / 3600);
      const minutes = (conference.duration - hours * 3600) / 60;

      this.setDurationTime([hours, minutes]);
    }

    this.fields.day.value = `${this.day + 1}`;

    this.isEdit = !!conference;

    makeObservable(this, {
      duration: computed,
      durationTime: observable,
      getConference: action.bound,
      onDurationChange: action.bound,
      setDurationTime: action.bound,
      title: computed,
    });
  }

  get duration() {
    const [hours, minutes] = this.durationTime;

    return hours! * 3600 + minutes! * 60;
  }

  get title() {
    /**
     * @i18n_parser
     *
     * t('shared:CRUProgramScheduleConference.title.create')
     * t('shared:CRUProgramScheduleConference.title.update')
     */
    return i18n.t(
      `shared:CRUProgramScheduleConference.title.${this.isEdit ? 'update' : 'create'}`,
    );
  }

  getConference() {
    if (this.isFormValid) {
      const conference: ScheduledConference = {
        day: this.day,
        description: this.fields.description.value,
        duration: this.duration,
        edition_uuid: this.uuid,
        repeat: {
          days: this.repeat.interval || null,
          type: this.repeat.interval ? 'interval' : 'once',
        },
        speaker_ids: [currentUserStore.user!._id],
        time: getSecondsFromMidnight(this.date),
        title: this.fields.title.value,
        uuid: this.groupUuid,
      };

      return conference;
    }
  }

  onDurationChange(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    const durationString = e.payload?.text;

    if (durationString) {
      const [hoursString, minsString] = durationString.split(':');

      const hours = parseInt(hoursString || '0', 10);
      const mins = parseInt(minsString || '0', 10);

      const maxHour =
        CRUProgramScheduleConferenceStore.DURATION_HOURS.slice().pop()!;
      const maxMins =
        CRUProgramScheduleConferenceStore.DURATION_MINUTES.slice().pop()!;

      if (hours >= maxHour) {
        this.setDurationTime([maxHour, 0]);
      } else if (mins >= maxMins) {
        this.setDurationTime([hours, maxMins]);
      } else if (hours === 0 && mins === 0) {
        this.setDurationTime([0, 15]);
      } else {
        this.setDurationTime([
          isNaN(hours) ? 1 : hours,
          isNaN(mins) ? 0 : mins,
        ]);
      }
    }
  }

  setDurationTime(durationTime: [number, number]) {
    this.durationTime = durationTime;
  }
}
